import { useState } from 'react';

import { useExportEvents, BaseExportEvent } from '@/api/hooks/useExportEvents';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface Props {
  /** Callback to trigger the download when complete */
  onDownload: () => void;
  /** Error message if creation failed */
  error?: string;
  /** Whether the export already exists and is ready */
  exportReady: boolean;
  /** Name of the item being exported (for display) */
  itemName: string;
  /** Optional: Additional description for the ready state */
  readyDescription?: string;
  /** Progress percentage (0-100) */
  progress?: number;
  /** Total number of documents being processed */
  totalDocuments?: number;
  /** Number of documents processed so far */
  processedDocuments?: number;
  /** Optional: Custom text for download button (defaults to "Download") */
  downloadButtonText?: string;
  /** Optional: Callback to retry on error (shows "Try Again" button) */
  onRetry?: () => void;
  /** Optional: Custom text for creating state */
  creatingText?: string;
}

export function ExportProgressContent({
  onDownload,
  error,
  exportReady,
  itemName,
  readyDescription,
  progress = 0,
  totalDocuments = 0,
  processedDocuments = 0,
  downloadButtonText = 'Download',
  onRetry,
  creatingText = 'Creating export...',
}: Props) {
  // Error state
  if (error) {
    return (
      <div className="flex h-72 flex-col items-center justify-center gap-4 px-8 py-6">
        <div className="text-xl font-medium text-red-600">Error creating export</div>
        <div className="text-center text-gray-600">{error}</div>
        {onRetry && (
          <Button onClick={onRetry} className="mt-4">
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // Ready state
  if (exportReady) {
    return (
      <div className="flex flex-col items-center justify-center gap-6">
        <div className="flex flex-col items-center gap-2">
          <Icon className="lucide--check-circle text-4xl text-teal-500" />
          <div className="text-lg font-medium text-gray-700">Export ready for download</div>
        </div>

        <div className="max-w-sm text-center text-gray-600">
          {readyDescription || `${itemName} with source documents has been packaged.`}
          {totalDocuments > 0 && (
            <div className="text-medium mt-2 text-sm text-gray-500">
              {totalDocuments} document{totalDocuments !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        <Button variant="primary" onClick={onDownload} className="w-full justify-center py-2.5">
          {downloadButtonText}
        </Button>
      </div>
    );
  }

  // Creating state - always show progress bar (no separate "preparing" state)
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="flex flex-col items-center gap-2">
        <Icon className="lucide--archive text-4xl text-teal-500" />
        <div className="text-lg font-medium text-gray-700">{creatingText}</div>
      </div>

      <div className="w-full max-w-md">
        <div className="mb-2 flex justify-between text-sm text-gray-600">
          <span>Progress</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-gray-200">
          <div
            className="bg-brandSecondary h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        {totalDocuments > 0 && (
          <div className="mt-2 text-center text-sm text-gray-600">
            Processing {processedDocuments} of {totalDocuments} document
            {totalDocuments !== 1 ? 's' : ''}
          </div>
        )}
      </div>
      <div className="text-center text-sm text-gray-600">
        This may take a few minutes depending on the number and size of documents.
      </div>
    </div>
  );
}

/**
 * Configuration for export endpoints
 */
export interface ExportConfig {
  /** Base endpoint for progress SSE */
  progressEndpoint: string;
}

/**
 * Props for GenericExportProgressModal
 */
interface GenericExportProgressModalProps<TEvent extends BaseExportEvent> {
  // Modal control
  isOpen: boolean;
  onClose: () => void;

  // Export configuration
  exportConfig: ExportConfig;

  // Display & behavior
  itemName: string;
  onDownload: () => Promise<void>;
  isCreating: boolean;
  error?: string;

  // Optional customization
  readyDescription?: string;
  downloadButtonText?: string;
  onRetry?: () => void;
  creatingText?: string;
}

export const GenericExportProgressModal = <TEvent extends BaseExportEvent>({
  isOpen,
  onClose,
  exportConfig,
  itemName,
  onDownload,
  isCreating,
  error,
  readyDescription,
  downloadButtonText,
  onRetry,
  creatingText,
}: GenericExportProgressModalProps<TEvent>) => {
  const [exportReady, setExportReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [processedDocuments, setProcessedDocuments] = useState(0);

  useExportEvents<TEvent>({
    progressEndpoint: exportConfig.progressEndpoint,
    onEvent: (event) => {
      if (event.type.includes('progress')) {
        setProgress(event.progress || 0);
        if (event.totalDocuments) setTotalDocuments(event.totalDocuments);
        if (event.processedDocuments !== undefined) setProcessedDocuments(event.processedDocuments);
      } else if (event.type.includes('complete')) {
        setProgress(100);
        setExportReady(true);
      }
    },
    enabled: isOpen && !exportReady,
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Exporting {itemName}</DialogTitle>
        </DialogHeader>

        <ExportProgressContent
          onDownload={onDownload}
          error={error}
          exportReady={exportReady}
          itemName={itemName}
          readyDescription={readyDescription}
          progress={progress}
          totalDocuments={totalDocuments}
          processedDocuments={processedDocuments}
          downloadButtonText={downloadButtonText}
          onRetry={onRetry}
          creatingText={creatingText}
        />
      </DialogContent>
    </Dialog>
  );
};
